import React, { useCallback, useEffect, useState } from 'react';
import { SkeletonRows } from '../ui/Skeleton';
import { PageHeader } from '../ui/PageHeader';
import { ErrorBanner } from '../ui/ErrorBanner';
import { ConfirmModal } from '@/components/ConfirmModal';
import { supabase } from '../lib/supabase';
import { timeAgo } from '../lib/timeAgo';
import { venueDisplayName } from '../lib/growth';

type ProfileTarget = { kind: 'city' | 'university'; value: string; n: number };

type AnnouncementRow = {
  id: string;
  body: string;
  is_active: boolean;
  blocking: boolean;
  require_terms: boolean;
  created_at: string;
  dismissal_count: number | string;
};

/** One past push send with attributed outcomes — admin_push_campaign_stats (v4). */
type CampaignStat = {
  id: string;
  sent_at: string;
  body: string;
  campaign: string | null;
  recipient_count: number;
  is_test: boolean;
  attributed: boolean;
  opened_72h: number;
  rated_7d: number;
  target_city: string | null;
  target_university: string | null;
};

/**
 * One row of scheduled_announcement_pushes. `params` is the EXACT named-args object the send
 * button would have passed to admin_push_announcement_audience — run_due_announcement_pushes()
 * (pg_cron, every minute) replays it later AS THE ADMIN WHO SCHEDULED IT, so the audience is
 * whoever matches the filters at fire time and the log attributes the send to that admin.
 */
type ScheduledPushRow = {
  id: number;
  created_at: string;
  run_at: string;
  params: Record<string, unknown>;
  status: 'scheduled' | 'sending' | 'sent' | 'failed' | 'cancelled';
  result: string | null;
  sent_at: string | null;
};

type LookupResult = {
  user: {
    username: string | null;
    joined_at: string;
    last_rated_at: string | null;
    last_seen_at: string | null;
    venues_rated: number;
    has_push_token: boolean;
  };
  notifications: { message: string; campaign: string | null; at: string }[];
};

function pushLogMissing(message: string): boolean {
  const m = message.toLowerCase();
  return (
    m.includes('admin_push_campaign_stats') || m.includes('admin_list_push_announcement_log') || m.includes('42883') || m.includes('could not find the function')
  );
}

type AudienceSample = {
  username: string | null;
  joined_at: string;
  last_rated_at: string | null;
  last_seen_at: string | null;
  pushes_received: number | null;
};

/** Retention red-flag presets — each loads an audience + suggested push copy. */
type EngagementPreset = {
  label: string;
  description: string;
  copy: string;
  campaign: string;
  filters: {
    minVenues?: number;
    maxVenues?: number;
    lastRatingDays?: number;
    lastSeenDays?: number;
    joinedAfterDaysAgo?: number;
  };
};

const ENGAGEMENT_PRESETS: EngagementPreset[] = [
  {
    label: 'Never rated',
    campaign: 'never-rated-v1',
    description: 'Signed up but has zero Shado ratings.',
    copy: 'Your first rating takes 10 seconds — where did you go out last? Rank it and see where it lands.',
    filters: { minVenues: 0, maxVenues: 0 },
  },
  {
    label: 'New & silent',
    campaign: 'new-silent-v1',
    description: 'Joined in the last 7 days, still no rating.',
    copy: 'Welcome to Shado — rate your first bar and unlock your city’s real rankings.',
    filters: { minVenues: 0, maxVenues: 0, joinedAfterDaysAgo: 7 },
  },
  {
    label: 'Lapsed rater · 14d',
    campaign: 'lapsed-14-v1',
    description: 'Has rated before, nothing in 14+ days.',
    copy: 'Your city’s rankings moved since your last rating. See what’s #1 now.',
    filters: { lastRatingDays: 14 },
  },
  {
    label: 'Lapsed rater · 30d',
    campaign: 'lapsed-30-v1',
    description: 'Has rated before, nothing in 30+ days.',
    copy: 'It’s been a minute — your top bar might’ve been dethroned. Check the rankings.',
    filters: { lastRatingDays: 30 },
  },
  {
    label: 'Gone quiet · 14d',
    campaign: 'gone-quiet-v1',
    description: 'Hasn’t opened the app in 14+ days.',
    copy: 'The rankings didn’t stop while you were gone. See what changed.',
    filters: { lastSeenDays: 14 },
  },
  {
    label: 'One-and-done',
    campaign: 'one-and-done-v1',
    description: 'Rated exactly one venue, then stopped (14+ days).',
    copy: 'You ranked one spot — add a second and your personal leaderboard starts to mean something.',
    filters: { minVenues: 1, maxVenues: 1, lastRatingDays: 14 },
  },
];

function daysAgoDateInput(n: number): string {
  return new Date(Date.now() - n * 86400000).toISOString().slice(0, 10);
}

function presetRpcParams(p: EngagementPreset): Record<string, unknown> {
  const params: Record<string, unknown> = {};
  if (p.filters.joinedAfterDaysAgo != null) {
    params.p_joined_after = new Date(daysAgoDateInput(p.filters.joinedAfterDaysAgo)).toISOString();
  }
  if (p.filters.minVenues != null) params.p_min_venues_rated = p.filters.minVenues;
  if (p.filters.maxVenues != null) params.p_max_venues_rated = p.filters.maxVenues;
  if (p.filters.lastRatingDays != null) params.p_last_rating_older_than_days = p.filters.lastRatingDays;
  if (p.filters.lastSeenDays != null) params.p_last_seen_older_than_days = p.filters.lastSeenDays;
  return params;
}

function truncate(s: string, n: number): string {
  const t = s.trim();
  if (t.length <= n) return t;
  return `${t.slice(0, n)}…`;
}

// "" -> not set (off). Non-numeric / negative -> not set. Otherwise a whole,
// non-negative integer (matches count_distinct_venues_for_user()'s integer count).
function parseVenuesInput(v: string): number | null {
  const t = v.trim();
  if (!t) return null;
  const n = Number(t);
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.trunc(n);
}

/**
 * ── THE FIRST LINE ────────────────────────────────────────────────────────────────────────────
 * The push title. It was hardcoded to "shado" in `send-push-notification/index.ts` since R3-1a, so
 * a sender controlled only the SECOND line; `p_title` now carries an override per send. Blank keeps
 * the brand title, which is what every announcement before this used.
 *
 * The cap matches `PUSH_TITLE_MAX` in that Edge function, which clamps again server-side: iOS gives
 * the title about one line and silently swallows the rest, taking the body off the banner with it.
 */
const PUSH_TITLE_MAX = 64;
/** What actually FITS on the lock screen's top row at default zoom on Evan's iPhone — measured
 * by him, 2026-10-09: "end of last word should be within 28 characters". The hard cap above is
 * the server clamp; this is the visual budget the counter and preview warn against. */
const PUSH_TITLE_FIT = 28;

/**
 * ── THE TAP DESTINATION ───────────────────────────────────────────────────────────────────────
 * Evan 2026-10-05: "ideally choose what clicking that notification does."
 *
 * ⚠️ EVERY OPTION HERE MUST BE ONE THE SHIPPED APP CAN ACTUALLY HONOUR. A dropdown offering a route
 * the client ignores is worse than no dropdown — the tap silently does nothing and nobody ever
 * finds out. That is not hypothetical: `announcement` is the highest-volume notification type in
 * production (19,514 rows on 2026-10-05, eight times the next) and has never had a case in
 * `routeFromPushData` (shado `app/_layout.tsx`), so every broadcast tap ever sent has landed on the
 * notification centre regardless of what the copy promised.
 *
 * These four map 1:1 onto `_announcement_route_normalized()` in the shado repo's
 * `supabase/migrations/20261005000000_push_announcement_title_and_route.sql`, which re-validates
 * them server-side and RAISES on anything it does not recognise. Adding an option here without
 * adding it there — and without a client case that honours it — fails the send loudly instead of
 * shipping another dead tap.
 */
type RouteKind = 'notifications' | 'tab' | 'venue' | 'city';

/**
 * The four tabs with an `href` in `app/(tabs)/_layout.tsx:602-605`. `index` and `explore` are
 * `href: null` and `shop` is behind `FEATURE_SHOP_AND_REFERRALS_UI` — none are reachable.
 */
const ROUTE_TAB_OPTIONS = [
  { value: 'discover', label: 'Discover — the map' },
  { value: 'search', label: 'Search' },
  { value: 'feed', label: 'Feed' },
  { value: 'me', label: 'Their own profile' },
] as const;

/**
 * Named non-tab screens (Evan 2026-10-12: "open find friends page, or new ratings page, all
 * options"). Slugs must stay identical to _announcement_route_normalized (v8) and the Edge
 * ROUTE_SCREENS; the app maps slug → path in its push router (new OTA case — until a user runs
 * a build with it, the tap degrades to the notification centre, never breaks).
 */
const ROUTE_SCREEN_OPTIONS = [
  { value: 'find_friends', label: 'Find friends' },
  { value: 'new_rating', label: 'Rate a venue — the new-rating flow' },
  { value: 'people', label: 'People — their friends list' },
  { value: 'wishlist', label: 'Their wishlist' },
] as const;

type RouteVenue = {
  id: string;
  name: string;
  cityLabel: string;
  google_place_id: string | null;
  apple_place_id: string | null;
};

type RouteCity = { id: string; name: string; formatted: string };

type RouteVenueRow = {
  id: string;
  venue_name_user_input: string | null;
  osm_name: string | null;
  locality: string | null;
  region: string | null;
  google_place_id: string | null;
  apple_place_id: string | null;
};

type RouteCityRow = {
  id: string;
  name: string;
  region: string | null;
  country_code: string | null;
};

/**
 * PostgREST's `or=` filter is a comma/paren-delimited mini-language, so a typed comma or bracket
 * would split the filter rather than be searched for — a 400 at best and the wrong rows at worst.
 * Strip the delimiters and the ILIKE wildcards instead of trying to quote them; nobody searches a
 * venue by punctuation.
 */
function ilikeSafe(q: string): string {
  return q.replace(/[,()%*\\]/g, ' ').trim();
}

function countryDisplayName(countryCode: string | null): string {
  const cc = (countryCode ?? '').trim().toUpperCase();
  if (!cc) return '';
  try {
    return new Intl.DisplayNames(['en'], { type: 'region' }).of(cc) ?? cc;
  } catch {
    return cc;
  }
}

/**
 * Mirrors `discoverCityFormattedLabel` in the app (`app/(tabs)/discover/index.tsx:719`) so the
 * label stored on the route is the same string the city page titles itself with: US cities get the
 * state code, everywhere else the country name, and a city whose name already ends in the suffix
 * ("Washington DC") does not get it twice.
 */
function cityFormattedLabel(
  name: string,
  countryCode: string | null,
  region: string | null,
): string {
  const city = (name ?? '').trim();
  const cc = (countryCode ?? '').trim().toUpperCase();
  const reg = (region ?? '').trim().toUpperCase();
  const suffix = cc === 'US' && reg.length >= 2 && reg.length <= 3 ? reg : countryDisplayName(cc);
  if (!suffix) return city;
  const cityU = city.toUpperCase();
  const sufU = suffix.toUpperCase();
  if (cityU === sufU || cityU.endsWith(` ${sufU}`)) return city;
  return `${city}, ${suffix}`;
}

/**
 * The 21-arg `admin_push_announcement_audience` (title + destination) is not applied yet. PostgREST
 * reports an unresolvable named-arg set as PGRST202 / "Could not find the function … in the schema
 * cache", which reads like the whole announcements feature is broken rather than like one migration
 * is pending — so name the migration in the message.
 */
function pushContentRpcMissing(message: string): boolean {
  const m = message.toLowerCase();
  if (m.includes('p_title') || m.includes('p_route')) return true;
  return (
    (m.includes('pgrst202') || m.includes('could not find the function')) &&
    m.includes('admin_push_announcement_audience')
  );
}

const PUSH_CONTENT_RPC_HINT =
  'A custom title or tap destination needs the newer send function. Apply ' +
  'supabase/migrations/20261005000000_push_announcement_title_and_route.sql in the shado repo ' +
  '(supabase db query --linked -f …), then retry. Clearing both fields sends on the old path.';

function sendErrorMessage(e: unknown, fallback: string): string {
  const msg = e instanceof Error ? e.message : fallback;
  return pushContentRpcMissing(msg) ? `${msg} — ${PUSH_CONTENT_RPC_HINT}` : msg;
}

// Additive: render "X / Y dismissed (Z%)" plus a thin progress bar.
// Guards divide-by-zero and an unknown total (falls back to the plain count).
function DismissalRate({
  dismissed,
  total,
  className,
}: {
  dismissed: number;
  total: number | null;
  className?: string;
}) {
  const safeDismissed = Number.isFinite(dismissed) ? dismissed : 0;
  if (total == null || total <= 0) {
    return (
      <span className={className}>{safeDismissed.toLocaleString()} dismissed</span>
    );
  }
  const pct = Math.max(0, Math.min(100, (safeDismissed / total) * 100));
  return (
    <span className={className}>
      <span className="tabular-nums">
        {safeDismissed.toLocaleString()} / {total.toLocaleString()} dismissed (
        {pct.toFixed(pct < 10 ? 1 : 0)}%)
      </span>
      <span className="mt-1 block h-1 w-full max-w-[260px] overflow-hidden rounded-full bg-white/[0.08]">
        <span
          className="block h-full rounded-full bg-accent"
          style={{ width: `${pct}%` }}
        />
      </span>
    </span>
  );
}

export function AnnouncementsScreen() {
  const [draft, setDraft] = useState('');
  /**
   * The push's FIRST line. Blank = the brand title "shado", i.e. exactly what every announcement
   * sent before 2026-10-05 looked like. Push-path only: the in-app overlay has no title slot.
   */
  const [title, setTitle] = useState('');
  // Where the push lands when it is tapped. Default = what it already does today.
  const [routeKind, setRouteKind] = useState<RouteKind>('notifications');
  const [routeTab, setRouteTab] = useState<string>('discover');
  const [routeVenue, setRouteVenue] = useState<RouteVenue | null>(null);
  const [routeCity, setRouteCity] = useState<RouteCity | null>(null);
  const [routeQuery, setRouteQuery] = useState('');
  const [routeResults, setRouteResults] = useState<(RouteVenue | RouteCity)[]>([]);
  const [routeSearchBusy, setRouteSearchBusy] = useState(false);
  const [blocking, setBlocking] = useState(false);
  const [requireTerms, setRequireTerms] = useState(false);
  const [confirmBlockingOpen, setConfirmBlockingOpen] = useState(false);
  // "Send as push" path: flips the output from in-app overlay → push notification to the same segment.
  // Default ON (Evan 2026-09-08): announcements are almost always pushes now.
  const [sendAsPush, setSendAsPush] = useState(true);
  const [confirmPushOpen, setConfirmPushOpen] = useState(false);
  // Audience count fetched FRESH at confirm-open with the current filters, so the number the admin
  // confirms is exactly who gets pushed (the modal freezes the filters until confirm/cancel).
  const [pushAudience, setPushAudience] = useState<number | null>(null);
  // Scheduled pushes (Evan 2026-10-07): send later, with the audience evaluated AT SEND TIME.
  const [scheduleAt, setScheduleAt] = useState('');
  const [scheduleBusy, setScheduleBusy] = useState(false);
  const [scheduled, setScheduled] = useState<ScheduledPushRow[]>([]);
  const [scheduledUnavailable, setScheduledUnavailable] = useState(false);
  const [cancelBusyId, setCancelBusyId] = useState<number | null>(null);
  const [history, setHistory] = useState<AnnouncementRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pushHistory, setPushHistory] = useState<CampaignStat[]>([]);
  const [pushHistoryLoading, setPushHistoryLoading] = useState(true);
  const [pushHistoryUnavailable, setPushHistoryUnavailable] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [userCountBusy, setUserCountBusy] = useState(false);
  // Additive: total user count fetched once on load, used as the dismissal denominator.
  const [totalUsers, setTotalUsers] = useState<number | null>(null);

  // Specific-user targeting (Evan 2026-10-21: "send a notification to a single person or list
  // of individual users") — picked users ride p_only_user_ids through count/sample/push/schedule.
  const [targetUsers, setTargetUsers] = useState<{ id: string; username: string }[]>([]);
  const [userQuery, setUserQuery] = useState('');
  const [userResults, setUserResults] = useState<{ id: string; username: string }[]>([]);

  // Audience targeting filters
  const [joinedBefore, setJoinedBefore] = useState('');
  const [joinedAfter, setJoinedAfter] = useState('');
  const [cityFilter, setCityFilter] = useState('');
  const [universityFilter, setUniversityFilter] = useState('');
  /** How city + university combine when BOTH are set (Evan 2026-10-07): AND = that uni's people
   * in that city; OR = everyone in the city plus everyone at the uni. */
  const [cityUniLogic, setCityUniLogic] = useState<'and' | 'or'>('and');
  // Total distinct venues rated, same definition as count_distinct_venues_for_user()
  // (distinct venues from the user's own non-retired, non-event logs). Empty = off.
  const [minVenuesRated, setMinVenuesRated] = useState('');
  /**
   * Friends = MUTUAL follows, matching the app's own definition and `friends_count` in
   * `admin_list_users`. A one-way follow is not a friend anywhere else in the product.
   * Distribution on prod 2026-08-18: 0 → 219 users, 1-4 → 132, 5-9 → 62, 10-24 → 54, 25+ → 13.
   */
  const [minFriends, setMinFriends] = useState('');
  const [maxFriends, setMaxFriends] = useState('');
  const [maxVenuesRated, setMaxVenuesRated] = useState('');
  // Retention red-flag filters (push path — the publish RPC keeps its original signature)
  const [lastRatingDays, setLastRatingDays] = useState('');
  const [lastSeenDays, setLastSeenDays] = useState('');
  const [reachableOnly, setReachableOnly] = useState(false);
  // Frequency cap: skip anyone already pushed within the window. Free entry in HOURS or DAYS
  // (1h / 5h / 1d / anything — Evan 2026-08-15). Default ON at 7 days — the anti-spam rail.
  // Default EMPTY = no recency blocking (Evan 2026-09-08) — the cap is opt-in per send.
  const [freqCapValue, setFreqCapValue] = useState('');
  /** Target users who have EARNED a profile stamp — evaluated server-side over their logs. */
  const [stampFilter, setStampFilter] = useState('');
  const [freqCapUnit, setFreqCapUnit] = useState<'hours' | 'days'>('days');
  const [activePreset, setActivePreset] = useState<string | null>(null);
  // Campaign identity + dedupe/retarget/pressure controls (v4)
  const [campaign, setCampaign] = useState('');
  const [dedupeSameCampaign, setDedupeSameCampaign] = useState(true);
  const [retargetCampaign, setRetargetCampaign] = useState('');
  const [maxPushesTotal, setMaxPushesTotal] = useState('');
  const [lookupQuery, setLookupQuery] = useState('');
  const [lookupResult, setLookupResult] = useState<LookupResult | null | 'not-found'>(null);
  const [lookupBusy, setLookupBusy] = useState(false);
  const [historyTab, setHistoryTab] = useState<'push' | 'in-app'>('push');
  const [presetCounts, setPresetCounts] = useState<Record<string, number | null>>({});
  const [samples, setSamples] = useState<AudienceSample[]>([]);
  const [testBusy, setTestBusy] = useState(false);
  const [profileTargets, setProfileTargets] = useState<ProfileTarget[]>([]);
  const [audienceCount, setAudienceCount] = useState<number | null>(null);
  const [audienceCountBusy, setAudienceCountBusy] = useState(false);

  const active = history.find((r) => r.is_active) ?? null;
  const knownCampaigns = Array.from(
    new Set(pushHistory.map((r) => r.campaign).filter((c): c is string => Boolean(c))),
  );

  // Shared audience-filter params for admin_count_announcement_audience /
  // admin_publish_global_announcement / admin_push_announcement_audience.
  // p_min_venues_rated / p_max_venues_rated are OMITTED entirely (not sent as
  // null) when both inputs are empty, so this keeps working against the RPCs
  // as currently deployed (4/7-param signatures, no venues-rated params) —
  // PostgREST resolves an RPC call by the exact set of named args present, so
  // sending an unknown key 400s until the SQL in
  // supabase/sql/announcement_audience_min_venues_rated.sql is applied.
  const audienceParams = useCallback((): Record<string, unknown> => {
    const min = parseVenuesInput(minVenuesRated);
    const max = parseVenuesInput(maxVenuesRated);
    const params: Record<string, unknown> = {
      p_joined_before: joinedBefore ? new Date(joinedBefore).toISOString() : null,
      p_joined_after: joinedAfter ? new Date(joinedAfter).toISOString() : null,
      p_city: cityFilter || null,
      p_university: universityFilter || null,
      p_city_university_logic: cityFilter && universityFilter ? cityUniLogic : 'and',
    };
    if (min !== null) params.p_min_venues_rated = min;
    if (max !== null) params.p_max_venues_rated = max;
    /**
     * ⚠️ OMITTED WHEN BLANK, never sent as null. PostgREST resolves an RPC by the exact set of named
     * args present, so sending a key the deployed function does not have 400s the whole call — which
     * is why every optional filter here is conditional rather than always-present.
     */
    const minF = parseVenuesInput(minFriends);
    const maxF = parseVenuesInput(maxFriends);
    if (minF !== null) params.p_min_friends = minF;
    if (maxF !== null) params.p_max_friends = maxF;
    return params;
  }, [joinedBefore, joinedAfter, cityFilter, universityFilter, cityUniLogic, minVenuesRated, maxVenuesRated, minFriends, maxFriends]);

  useEffect(() => {
    const q = userQuery.trim();
    if (q.length < 2) {
      setUserResults([]);
      return;
    }
    const t = window.setTimeout(() => {
      void supabase
        .from('profiles')
        .select('id, username')
        .ilike('username', `%${q}%`)
        .not('username', 'is', null)
        .limit(8)
        .then(({ data }) => {
          const picked = new Set(targetUsers.map((u) => u.id));
          setUserResults(
            (((data ?? []) as { id: string; username: string | null }[])
              .filter((r) => r.username && !picked.has(r.id)) as { id: string; username: string }[]),
          );
        });
    }, 250);
    return () => window.clearTimeout(t);
  }, [userQuery, targetUsers]);

  // Push-path params: base filters + retention red flags + reachability + frequency cap.
  // Used by the live count, the sample preview, the confirm, and the send — always together,
  // so the number you confirm is exactly who gets pushed.
  const pushParams = useCallback((): Record<string, unknown> => {
    const params = audienceParams();
    const lr = parseVenuesInput(lastRatingDays);
    const ls = parseVenuesInput(lastSeenDays);
    const cap = parseVenuesInput(freqCapValue);
    if (lr !== null && lr > 0) params.p_last_rating_older_than_days = lr;
    if (ls !== null && ls > 0) params.p_last_seen_older_than_days = ls;
    if (reachableOnly) params.p_has_push_token = true;
    if (stampFilter) params.p_stamp = stampFilter;
    if (cap !== null && cap > 0) {
      // v5 RPCs take hours — days are converted here so the SQL stays one param.
      params.p_not_pushed_within_hours = cap * (freqCapUnit === 'days' ? 24 : 1);
    }
    if (targetUsers.length > 0) params.p_only_user_ids = targetUsers.map((u) => u.id);
    const camp = campaign.trim();
    if (dedupeSameCampaign && camp) params.p_exclude_campaign = camp;
    if (retargetCampaign.trim()) params.p_received_campaign = retargetCampaign.trim();
    const mp = parseVenuesInput(maxPushesTotal);
    if (mp !== null && mp > 0) params.p_max_pushes_total = mp;
    return params;
  }, [audienceParams, lastRatingDays, lastSeenDays, reachableOnly, freqCapValue, freqCapUnit, stampFilter, campaign, dedupeSameCampaign, retargetCampaign, maxPushesTotal, targetUsers]);

  /**
   * The chosen destination as the server expects it, or null for "the notification centre" — which
   * is what an announcement tap already does, so null means "change nothing".
   *
   * A half-filled picker (kind chosen, nothing selected) also returns null rather than a malformed
   * route: the send still goes out, it just lands where it always did. The UI blocks the send
   * button in that state, so this is the belt to that braces.
   */
  const routePayload = useCallback((): Record<string, unknown> | null => {
    if (routeKind === 'tab') {
      if (!routeTab) return null;
      if (routeTab.startsWith('screen:')) {
        return { kind: 'screen', screen: routeTab.slice('screen:'.length) };
      }
      return { kind: 'tab', tab: routeTab };
    }
    if (routeKind === 'venue') {
      if (!routeVenue) return null;
      return {
        kind: 'venue',
        venue_id: routeVenue.id,
        ...(routeVenue.google_place_id ? { google_place_id: routeVenue.google_place_id } : {}),
        ...(routeVenue.apple_place_id ? { apple_place_id: routeVenue.apple_place_id } : {}),
        venue_name: routeVenue.name,
      };
    }
    if (routeKind === 'city') {
      if (!routeCity) return null;
      // `city_id` puts the tap on the city screen's warm path (straight to `venues by city_id`)
      // instead of a `cities` ILIKE plus a geocode. The picker reads it out of `cities` already.
      return {
        kind: 'city',
        city_id: routeCity.id,
        city_name: routeCity.name,
        city_formatted_name: routeCity.formatted,
      };
    }
    return null;
  }, [routeKind, routeTab, routeVenue, routeCity]);

  /**
   * ⚠️ OMITTED WHEN UNSET, NEVER SENT AS NULL — the same rule every optional filter above follows,
   * for the same reason: PostgREST resolves an RPC by the exact set of named args present, so a key
   * the deployed function does not have 400s the entire send.
   *
   * That makes the rollout safe in BOTH directions. This dashboard can deploy before the migration
   * is applied — a send with no title and no destination produces a byte-identical call to today's
   * — and the migration can be applied before this dashboard deploys, because both params default
   * to NULL. Only a send that actually uses the new fields needs the new SQL, and that case is
   * caught by `pushContentRpcMissing` with the apply command in the message.
   */
  const contentParams = useCallback((): Record<string, unknown> => {
    const params: Record<string, unknown> = {};
    const t = title.trim();
    if (t) params.p_title = t.slice(0, PUSH_TITLE_MAX);
    const route = routePayload();
    if (route) params.p_route = route;
    return params;
  }, [title, routePayload]);

  /** True when a destination kind is picked but its target is not — the send is blocked on it. */
  const routeIncomplete =
    (routeKind === 'venue' && !routeVenue) || (routeKind === 'city' && !routeCity);

  const routeSummary =
    routeKind === 'notifications'
      ? 'the notification centre'
      : routeKind === 'tab'
        ? (ROUTE_TAB_OPTIONS.find((o) => o.value === routeTab)?.label ??
           ROUTE_SCREEN_OPTIONS.find((o) => `screen:${o.value}` === routeTab)?.label ??
           routeTab)
        : routeKind === 'venue'
          ? (routeVenue ? `${routeVenue.name} — venue page` : 'a venue (none picked yet)')
          : (routeCity ? `${routeCity.formatted} — city page` : 'a city (none picked yet)');

  /**
   * Destination search — debounced, and deliberately NARROW on both sides.
   *
   * Venues are restricted to the ~951 rows with at least one Shado rating. `venues` holds ~3.3M
   * Foursquare rows and an unrated one opens an empty page; a push whose whole point is "go look at
   * this place" must not land on nothing. Cities are restricted to the 268 featured rows for the
   * same reason — those are the only ones with a real city page behind them.
   *
   * Both sets are small enough that an ILIKE capped at 12 rows is instant, so this needs no RPC and
   * no new index.
   */
  useEffect(() => {
    if (routeKind !== 'venue' && routeKind !== 'city') {
      setRouteResults([]);
      return;
    }
    const q = ilikeSafe(routeQuery);
    if (q.length < 2) {
      setRouteResults([]);
      setRouteSearchBusy(false);
      return;
    }
    let cancelled = false;
    setRouteSearchBusy(true);
    const handle = window.setTimeout(() => {
      void (async () => {
        try {
          if (routeKind === 'venue') {
            const { data } = await supabase
              .from('venues')
              .select(
                'id, venue_name_user_input, osm_name, locality, region, google_place_id, apple_place_id',
              )
              .gt('venue_shado_rating_count', 0)
              .or(`venue_name_user_input.ilike.%${q}%,osm_name.ilike.%${q}%`)
              .order('venue_shado_rating_count', { ascending: false })
              .limit(12);
            if (cancelled) return;
            setRouteResults(
              ((data ?? []) as RouteVenueRow[]).map((r) => ({
                id: r.id,
                name: venueDisplayName(r),
                cityLabel: [r.locality, r.region].filter(Boolean).join(', '),
                google_place_id: r.google_place_id,
                apple_place_id: r.apple_place_id,
              })),
            );
          } else {
            const { data } = await supabase
              .from('cities')
              .select('id, name, region, country_code')
              .eq('featured', true)
              .ilike('name', `%${q}%`)
              .order('total_shado_ratings', { ascending: false })
              .limit(12);
            if (cancelled) return;
            setRouteResults(
              ((data ?? []) as RouteCityRow[]).map((r) => ({
                id: r.id,
                name: r.name,
                formatted: cityFormattedLabel(r.name, r.country_code, r.region),
              })),
            );
          }
        } catch {
          if (!cancelled) setRouteResults([]);
        } finally {
          if (!cancelled) setRouteSearchBusy(false);
        }
      })();
    }, 250);
    return () => {
      cancelled = true;
      window.clearTimeout(handle);
    };
  }, [routeKind, routeQuery]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: rpcErr } = await supabase.rpc('admin_list_global_announcements', { p_limit: 30 });
      if (rpcErr) {
        if (
          rpcErr.message.includes('admin_list_global_announcements') ||
          rpcErr.message.includes('global_announcements')
        ) {
          throw new Error(
            'Announcements are not set up yet. Run supabase/sql/global_announcements.sql in Supabase SQL Editor.',
          );
        }
        throw rpcErr;
      }
      setHistory((data ?? []) as AnnouncementRow[]);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load announcements');
      setHistory([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const loadPushHistory = useCallback(async () => {
    setPushHistoryLoading(true);
    try {
      const { data, error: rpcErr } = await supabase.rpc('admin_push_campaign_stats', { p_limit: 30 });
      if (rpcErr) {
        if (pushLogMissing(rpcErr.message)) {
          setPushHistoryUnavailable(true);
          setPushHistory([]);
          return;
        }
        throw rpcErr;
      }
      setPushHistoryUnavailable(false);
      setPushHistory((data ?? []) as CampaignStat[]);
    } catch {
      setPushHistory([]);
    } finally {
      setPushHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadPushHistory();
  }, [loadPushHistory]);

  // Scheduled rows — upcoming plus the recent outcomes, so a failed replay is visible here
  // rather than only in the table. Table-missing (SQL not applied yet) degrades to a notice.
  const loadScheduled = useCallback(async () => {
    try {
      const { data, error: qErr } = await supabase
        .from('scheduled_announcement_pushes')
        .select('id, created_at, run_at, params, status, result, sent_at')
        .neq('status', 'cancelled')
        .order('run_at', { ascending: false })
        .limit(25);
      if (qErr) {
        if (/scheduled_announcement_pushes/i.test(qErr.message)) {
          setScheduledUnavailable(true);
          setScheduled([]);
          return;
        }
        throw qErr;
      }
      setScheduledUnavailable(false);
      setScheduled((data ?? []) as ScheduledPushRow[]);
    } catch {
      setScheduled([]);
    }
  }, []);

  useEffect(() => {
    void loadScheduled();
  }, [loadScheduled]);

  // Additive: fetch the total user count once on load (same head-count the
  // "Add current user count" button uses) to serve as the dismissal denominator.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const { count, error: cErr } = await supabase
          .from('profiles')
          .select('id', { count: 'exact', head: true });
        if (cErr) throw cErr;
        if (!cancelled) setTotalUsers(Number(count ?? 0));
      } catch {
        // Non-fatal: dismissal display falls back to the plain count.
        if (!cancelled) setTotalUsers(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Load city + university options once for the audience dropdowns.
  useEffect(() => {
    void (async () => {
      try {
        const { data, error: e } = await supabase.rpc('admin_distinct_profile_targets');
        if (e) throw e;
        setProfileTargets((data ?? []) as ProfileTarget[]);
      } catch {
        // Non-fatal: dropdowns stay empty (still functional — null = everyone).
      }
    })();
  }, []);

  // Debounced live audience count + sample preview — re-fires whenever any audience
  // filter changes. Push mode uses the extended (red-flag) params; publish mode uses the
  // base params, so the number always matches what the chosen send path will do.
  useEffect(() => {
    let cancelled = false;
    const timer = setTimeout(() => {
      void (async () => {
        setAudienceCountBusy(true);
        const params = pushParams();
        try {
          const [countRes, sampleRes] = await Promise.all([
            supabase.rpc('admin_count_announcement_audience', params),
            supabase.rpc('admin_sample_announcement_audience', { ...params, p_limit: 8 }),
          ]);
          if (cancelled) return;
          setAudienceCount(countRes.error ? null : (countRes.data as number));
          setSamples(
            sampleRes.error || !Array.isArray(sampleRes.data) ? [] : (sampleRes.data as AudienceSample[]),
          );
        } catch {
          if (!cancelled) {
            setAudienceCount(null);
            setSamples([]);
          }
        } finally {
          if (!cancelled) setAudienceCountBusy(false);
        }
      })();
    }, 350);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [pushParams]);

  // Live counts for the red-flag preset cards (raw pool — no cap/reachability applied).
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const entries = await Promise.all(
        ENGAGEMENT_PRESETS.map(async (p) => {
          try {
            const { data, error: e } = await supabase.rpc(
              'admin_count_announcement_audience',
              presetRpcParams(p),
            );
            return [p.label, e ? null : (data as number)] as const;
          } catch {
            return [p.label, null] as const;
          }
        }),
      );
      if (!cancelled) setPresetCounts(Object.fromEntries(entries));
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Apply a red-flag preset: load its filters, switch to push mode, suggest copy if empty.
  function applyPreset(p: EngagementPreset) {
    setJoinedBefore('');
    setJoinedAfter(p.filters.joinedAfterDaysAgo != null ? daysAgoDateInput(p.filters.joinedAfterDaysAgo) : '');
    setCityFilter('');
    setUniversityFilter('');
    setMinVenuesRated(p.filters.minVenues != null ? String(p.filters.minVenues) : '');
    setMaxVenuesRated(p.filters.maxVenues != null ? String(p.filters.maxVenues) : '');
    setLastRatingDays(p.filters.lastRatingDays != null ? String(p.filters.lastRatingDays) : '');
    setLastSeenDays(p.filters.lastSeenDays != null ? String(p.filters.lastSeenDays) : '');
    setSendAsPush(true);
    setBlocking(false);
    setRequireTerms(false);
    setActivePreset(p.label);
    setCampaign(p.campaign);
    setDedupeSameCampaign(true);
    setRetargetCampaign('');
    setDraft((d) => (d.trim() ? d : p.copy));
  }

  // "Have we already tried this on them?" — one user's full announcement history.
  async function runLookup() {
    const q = lookupQuery.trim();
    if (!q || lookupBusy) return;
    setLookupBusy(true);
    setError(null);
    try {
      const { data, error: rpcErr } = await supabase.rpc('admin_user_push_history', { p_username: q });
      if (rpcErr) throw rpcErr;
      setLookupResult(data ? (data as LookupResult) : 'not-found');
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Lookup failed');
    } finally {
      setLookupBusy(false);
    }
  }

  // Push the draft to ONLY the signed-in admin — verify tone/rendering before a real send.
  async function sendTestToMe() {
    const body = draft.trim();
    if (!body || testBusy) return;
    setTestBusy(true);
    setError(null);
    setNotice(null);
    try {
      const { data: u } = await supabase.auth.getUser();
      const uid = u?.user?.id;
      if (!uid) throw new Error('No session');
      const { error: rpcErr } = await supabase.rpc('admin_push_announcement_audience', {
        p_body: body,
        p_only_user_id: uid,
        // The test send carries the SAME title and destination as the real one — a test that drops
        // them would verify the one thing you were not changing.
        ...contentParams(),
      });
      if (rpcErr) throw rpcErr;
      setNotice('Test push sent to your account only — check your phone.');
      void loadPushHistory();
    } catch (e: unknown) {
      setError(sendErrorMessage(e, 'Test push failed'));
    } finally {
      setTestBusy(false);
    }
  }

  // Toggling blocking ON requires explicit confirmation; turning it OFF is free.
  function onToggleBlocking(next: boolean) {
    if (next) {
      setConfirmBlockingOpen(true);
    } else {
      setBlocking(false);
    }
  }

  async function publish() {
    const body = draft.trim();
    if (!body) return;
    setBusy(true);
    setError(null);
    try {
      const { error: rpcErr } = await supabase.rpc('admin_publish_global_announcement', {
        p_body: body,
        p_blocking: blocking,
        p_require_terms: requireTerms,
        ...audienceParams(),
      });
      if (rpcErr) throw rpcErr;
      setDraft('');
      setBlocking(false);
      setRequireTerms(false);
      setJoinedBefore('');
      setJoinedAfter('');
      setCityFilter('');
      setUniversityFilter('');
      setMinVenuesRated('');
      setMaxVenuesRated('');
      await load();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Publish failed');
    } finally {
      setBusy(false);
    }
  }

  // Push path step 1: re-fetch the audience count with the CURRENT filters, then open the confirm.
  // The confirm modal freezes the filters, so the number shown == who doPushSend() will push to
  // (admin_count_announcement_audience and admin_push_announcement_audience share an identical WHERE).
  async function openPushConfirm() {
    // `routeIncomplete` = a destination kind picked with no target chosen. `routePayload()` would
    // quietly return null and the push would land on the notification centre instead of where the
    // copy promised — the exact failure this feature exists to remove.
    if (!draft.trim() || busy || routeIncomplete) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const { data, error: cErr } = await supabase.rpc('admin_count_announcement_audience', pushParams());
      if (cErr) throw cErr;
      setPushAudience(typeof data === 'number' ? data : null);
      setConfirmPushOpen(true);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Could not compute audience');
    } finally {
      setBusy(false);
    }
  }

  // Push path step 2: send the draft as a push to the SAME segment. The filter mapping below is
  // byte-identical to openPushConfirm() above and to the live "~N users" counter, so there is no way
  // to push a different audience than the one just confirmed. Returns the actual number pushed.
  async function doPushSend() {
    const body = draft.trim();
    if (!body) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const { data, error: rpcErr } = await supabase.rpc('admin_push_announcement_audience', {
        p_body: body,
        p_campaign: campaign.trim() || null,
        ...pushParams(),
        ...contentParams(),
      });
      if (rpcErr) throw rpcErr;
      const sent = typeof data === 'number' ? data : null;
      setDraft('');
      setTitle('');
      setRouteKind('notifications');
      setRouteVenue(null);
      setRouteCity(null);
      setRouteQuery('');
      setBlocking(false);
      setRequireTerms(false);
      setSendAsPush(false);
      setJoinedBefore('');
      setJoinedAfter('');
      setCityFilter('');
      setUniversityFilter('');
      setMinVenuesRated('');
      setMaxVenuesRated('');
      setLastRatingDays('');
      setLastSeenDays('');
      setReachableOnly(false);
      setFreqCapValue('7');
      setFreqCapUnit('days');
      setActivePreset(null);
      setRetargetCampaign('');
      setMaxPushesTotal('');
      setTargetUsers([]);
      setUserQuery('');
      // campaign + dedupe stay set on purpose: a follow-up send automatically excludes
      // everyone who already got this campaign.
      setNotice(sent != null ? `Push sent to ${sent.toLocaleString()} user${sent === 1 ? '' : 's'}.` : 'Push sent.');
      void loadPushHistory();
    } catch (e: unknown) {
      setError(sendErrorMessage(e, 'Push failed'));
    } finally {
      setBusy(false);
    }
  }

  // Store the send for later: the row carries the IDENTICAL params object doPushSend() would
  // have passed right now, so what fires at run_at is the send that was on screen — body, title,
  // destination, campaign, every filter, the AND/OR combinator. The audience itself is resolved
  // at fire time (frequency cap and dedupe stamp included), which is the point of scheduling.
  async function schedulePush() {
    const body = draft.trim();
    if (!body || scheduleBusy || routeIncomplete || !scheduleAt) return;
    const runAt = new Date(scheduleAt);
    if (!Number.isFinite(runAt.getTime()) || runAt.getTime() <= Date.now()) {
      setError('Pick a schedule time in the future.');
      return;
    }
    setScheduleBusy(true);
    setError(null);
    setNotice(null);
    try {
      const params: Record<string, unknown> = {
        p_body: body,
        ...(campaign.trim() ? { p_campaign: campaign.trim() } : {}),
        ...pushParams(),
        ...contentParams(),
      };
      const { data, error: insErr } = await supabase
        .from('scheduled_announcement_pushes')
        .insert({ run_at: runAt.toISOString(), params })
        .select('id');
      if (insErr) throw insErr;
      if (!data || data.length === 0) throw new Error('Schedule wrote 0 rows — not saved.');
      setNotice(
        `Scheduled for ${runAt.toLocaleString()}. It sends to whoever matches the filters at that moment (within a minute of the time).`
      );
      setScheduleAt('');
      void loadScheduled();
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Could not schedule';
      setError(
        /scheduled_announcement_pushes/i.test(msg)
          ? 'Scheduling needs the SQL applied first: supabase db query --linked -f shado-admin/supabase/sql/scheduled_announcement_pushes_v1.sql'
          : msg
      );
    } finally {
      setScheduleBusy(false);
    }
  }

  // Cancel only wins while the row is still 'scheduled' — the status guard in the WHERE means a
  // row the runner has already claimed ('sending') cannot be yanked back mid-send, and the
  // .select('id') makes a 0-row write loud instead of a silent no-op (the reject-button lesson).
  async function cancelScheduled(id: number) {
    setCancelBusyId(id);
    setError(null);
    try {
      const { data, error: upErr } = await supabase
        .from('scheduled_announcement_pushes')
        .update({ status: 'cancelled' })
        .eq('id', id)
        .eq('status', 'scheduled')
        .select('id');
      if (upErr) throw upErr;
      if (!data || data.length === 0) {
        throw new Error('Could not cancel — it may have already started sending. Refreshing the list.');
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Cancel failed');
    } finally {
      setCancelBusyId(null);
      void loadScheduled();
    }
  }

  // Fetch the live total user count and append it under the message text.
  async function insertUserCount() {
    setUserCountBusy(true);
    setError(null);
    try {
      const { count, error: cErr } = await supabase
        .from('profiles')
        .select('id', { count: 'exact', head: true });
      if (cErr) throw cErr;
      const line = `Current users: ${Number(count ?? 0).toLocaleString()}`;
      setDraft((d) => (d.trim() ? `${d.replace(/\s+$/, '')}\n\n${line}` : line));
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Could not fetch user count');
    } finally {
      setUserCountBusy(false);
    }
  }

  // Additive: load a past announcement's body into the draft textarea for reuse.
  function reuseAsDraft(body: string) {
    setDraft(body);
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  async function endActive() {
    if (!active) return;
    setBusy(true);
    setError(null);
    try {
      const { error: rpcErr } = await supabase.rpc('admin_deactivate_global_announcements');
      if (rpcErr) throw rpcErr;
      await load();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Could not end announcement');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="admin-page max-w-3xl mx-auto">
      <PageHeader
        title="Announcements"
        subtitle={
          <>
            Publish a full-screen message to all signed-in users in the main Shado app. A normal message can be
            dismissed; a <span className="text-white">blocking</span> message cannot — it covers the app for
            everyone until you end it (use it for maintenance or outages). Publishing a new message replaces any
            live one.
          </>
        }
      />

      {error ? <ErrorBanner className="mb-5">{error}</ErrorBanner> : null}

      {notice ? (
        <div className="mb-5 rounded-lg border border-emerald-500/35 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-300">
          {notice}
        </div>
      ) : null}

      {loading ? (
        <SkeletonRows rows={5} className="max-w-3xl py-4" />
      ) : (
        <div className="space-y-8">
          

          <section className="rounded-xl border border-border bg-surface p-6 space-y-4">
            <h2 className="text-[10px] font-medium uppercase tracking-[0.14em] text-mutedDim">
              User lookup — what have we tried on them?
            </h2>
            <div className="flex gap-2">
              <input
                type="text"
                placeholder="username"
                value={lookupQuery}
                onChange={(e) => setLookupQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') void runLookup();
                }}
                className="admin-input !w-auto min-w-[200px]"
              />
              <button
                type="button"
                disabled={lookupBusy || !lookupQuery.trim()}
                onClick={() => void runLookup()}
                className="rounded-lg border border-border px-4 py-2 text-sm font-medium text-muted hover:text-white hover:bg-white/[0.04] disabled:opacity-40"
              >
                {lookupBusy ? 'Looking…' : 'Look up'}
              </button>
            </div>
            {lookupResult === 'not-found' ? (
              <p className="text-sm text-muted">No user with that username.</p>
            ) : lookupResult ? (
              <div className="space-y-3">
                <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 text-xs">
                  <span className="text-sm font-semibold text-white">@{lookupResult.user.username}</span>
                  <span className="text-mutedDim">joined {timeAgo(lookupResult.user.joined_at)}</span>
                  <span className="text-mutedDim">
                    {lookupResult.user.venues_rated} venue{lookupResult.user.venues_rated === 1 ? '' : 's'} rated
                  </span>
                  <span className="text-mutedDim">
                    {lookupResult.user.last_rated_at
                      ? `last rated ${timeAgo(lookupResult.user.last_rated_at)}`
                      : 'never rated'}
                  </span>
                  <span className="text-mutedDim">
                    {lookupResult.user.last_seen_at
                      ? `last seen ${timeAgo(lookupResult.user.last_seen_at)}`
                      : 'no session on record'}
                  </span>
                  <span className={lookupResult.user.has_push_token ? 'text-emerald-300/90' : 'text-amber-300/90'}>
                    {lookupResult.user.has_push_token ? 'reachable by push' : 'no push token'}
                  </span>
                </div>
                {lookupResult.notifications.length === 0 ? (
                  <p className="text-sm text-muted">Never received an announcement — clean slate.</p>
                ) : (
                  <ul className="rounded-lg border border-border/60 divide-y divide-border/40 overflow-hidden">
                    {lookupResult.notifications.map((n, i) => (
                      <li key={i} className="flex flex-wrap items-baseline gap-x-3 gap-y-1 px-4 py-2.5 text-xs">
                        <span className="shrink-0 text-mutedDim">{timeAgo(n.at)}</span>
                        {n.campaign ? (
                          <span className="shrink-0 rounded-md border border-accent/40 bg-accent/10 px-1.5 py-0.5 text-[10px] font-medium text-accent">
                            {n.campaign}
                          </span>
                        ) : null}
                        <span className="min-w-0 flex-1 text-muted">{truncate(n.message, 140)}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ) : null}
          </section>

          <section className="rounded-xl border border-border bg-surface p-6 space-y-4">
            <h2 className="text-[10px] font-medium uppercase tracking-[0.14em] text-mutedDim">
              Engagement presets — retention red flags
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {ENGAGEMENT_PRESETS.map((p) => (
                <button
                  key={p.label}
                  type="button"
                  onClick={() => applyPreset(p)}
                  className={`rounded-lg border px-3 py-2 text-left transition-colors ${
                    activePreset === p.label
                      ? 'border-accent/60 bg-accent/10'
                      : 'border-border bg-canvas/40 hover:bg-white/[0.03]'
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[12.5px] font-semibold text-white">{p.label}</span>
                    <span className="shrink-0 text-xs font-semibold tabular-nums text-accent">
                      {presetCounts[p.label] != null ? presetCounts[p.label]!.toLocaleString() : '—'}
                    </span>
                  </div>
                  <p className="mt-0.5 truncate text-[11px] text-mutedDim">{p.description}</p>
                </button>
              ))}
            </div>
          </section>

          <section className="rounded-xl border border-border bg-surface p-6 space-y-4">
            <h2 className="text-[10px] font-medium uppercase tracking-[0.14em] text-mutedDim">New announcement</h2>

            {/* The push's FIRST line. Push path only — the in-app overlay has no title slot. */}
            {sendAsPush ? (
              <label className="block space-y-1">
                <span className="flex items-baseline justify-between gap-2 text-xs text-muted">
                  <span>Title — first line on the lock screen</span>
                  <span
                    className={`tabular-nums text-[11px] ${
                      title.length > PUSH_TITLE_FIT ? 'text-red-400' : 'text-mutedDim'
                    }`}
                  >
                    {title.length}/{PUSH_TITLE_FIT}
                  </span>
                </span>
                <input
                  type="text"
                  value={title}
                  maxLength={PUSH_TITLE_MAX}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="shado"
                  className="admin-input"
                />
                <span className="block text-[11px] text-mutedDim">
                  Leave blank for the usual “shado”. The top row fits ~{PUSH_TITLE_FIT} characters at
                  default zoom — end your last word by then; the preview below shows what gets cut.
                </span>
              </label>
            ) : null}

            <label className="block space-y-1">
              {sendAsPush ? (
                <span className="text-xs text-muted">Body — second line on the lock screen</span>
              ) : null}
              <textarea
                rows={8}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder={
                  sendAsPush
                    ? 'Write the message users will see under the title…'
                    : 'Write the message users will see on a full-screen overlay…'
                }
                className="admin-input !h-auto !py-3 resize-y leading-relaxed"
              />
            </label>

            {/* Lock-screen preview — the two lines, exactly as they will be composed. */}
            {sendAsPush ? (
              <div className="rounded-lg border border-border/60 bg-canvas/60 px-4 py-3">
                <p className="text-[10px] font-medium uppercase tracking-[0.14em] text-mutedDim">
                  Lock screen
                </p>
                {/* Past-the-fold characters are struck out in red: that is the part iOS cuts. */}
                <p className="mt-2 text-sm font-semibold text-white">
                  {(() => {
                    const t = title.trim() || 'shado';
                    if (t.length <= PUSH_TITLE_FIT) return t;
                    return (
                      <>
                        {t.slice(0, PUSH_TITLE_FIT)}
                        <span className="text-red-400/70 line-through">{t.slice(PUSH_TITLE_FIT)}</span>
                      </>
                    );
                  })()}
                </p>
                <p className="whitespace-pre-wrap text-sm leading-snug text-white/80">
                  {draft.trim() || 'Your message…'}
                </p>
                <p className="mt-2 text-[11px] text-mutedDim">Tapping it opens {routeSummary}.</p>
              </div>
            ) : null}

            <label className="flex items-start gap-3 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={blocking}
                disabled={sendAsPush}
                onChange={(e) => onToggleBlocking(e.target.checked)}
                className="mt-0.5 h-4 w-4 shrink-0 rounded border-border bg-canvas text-accent focus:ring-2 focus:ring-accent focus:ring-offset-0 focus:ring-offset-surface"
              />
              <span className="text-sm text-white/90">
                Block app use
                <span className="block text-xs text-mutedDim mt-0.5 leading-relaxed">
                  Users cannot dismiss this and cannot use the app until you end it. For maintenance / outages only.
                </span>
              </span>
            </label>

            <label className="flex items-start gap-3 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={requireTerms}
                disabled={sendAsPush}
                onChange={(e) => setRequireTerms(e.target.checked)}
                className="mt-0.5 h-4 w-4 shrink-0 rounded border-border bg-canvas text-accent focus:ring-2 focus:ring-accent focus:ring-offset-0 focus:ring-offset-surface"
              />
              <span className="text-sm text-white/90">
                Require privacy / terms agreement
                <span className="block text-xs text-mutedDim mt-0.5 leading-relaxed">
                  Shows an "Agreed to our privacy policy and terms and conditions" checkbox (links open the in-app
                  popups); the user must check it before they can dismiss.
                </span>
              </span>
            </label>

            <label className="flex items-start gap-3 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={sendAsPush}
                onChange={(e) => {
                  const v = e.target.checked;
                  setSendAsPush(v);
                  if (v) {
                    setBlocking(false);
                    setRequireTerms(false);
                  }
                }}
                className="mt-0.5 h-4 w-4 shrink-0 rounded border-border bg-canvas text-accent focus:ring-2 focus:ring-accent focus:ring-offset-0 focus:ring-offset-surface"
              />
              <span className="text-sm text-white/90">
                Send as push notification (no in-app overlay)
                <span className="block text-xs text-mutedDim mt-0.5 leading-relaxed">
                  Sends the message as a push to the targeted audience's devices instead of showing the in-app
                  overlay. Uses the same audience filters below — you'll confirm the recipient count before it
                  sends.
                </span>
              </span>
            </label>

            {/* ── Tap destination ──────────────────────────────────────────────────────────────
                Push path only. The in-app overlay is already on screen, so there is nothing to
                route to. */}
            {sendAsPush ? (
              <div className="space-y-3 rounded-lg border border-border/60 bg-canvas/40 px-4 py-4">
                <p className="text-[10px] font-medium uppercase tracking-[0.14em] text-mutedDim">
                  When they tap it
                </p>
                <select
                  value={routeKind}
                  onChange={(e) => {
                    setRouteKind(e.target.value as RouteKind);
                    setRouteQuery('');
                    setRouteVenue(null);
                    setRouteCity(null);
                  }}
                  className="admin-input"
                >
                  <option value="notifications">Their notification centre (default)</option>
                  <option value="tab">A tab or page in the app</option>
                  <option value="venue">A specific venue's page</option>
                  <option value="city">A specific city's page</option>
                </select>

                {routeKind === 'tab' ? (
                  <select
                    value={routeTab}
                    onChange={(e) => setRouteTab(e.target.value)}
                    className="admin-input"
                  >
                    <optgroup label="Tabs">
                      {ROUTE_TAB_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </optgroup>
                    <optgroup label="Pages">
                      {ROUTE_SCREEN_OPTIONS.map((o) => (
                        <option key={o.value} value={`screen:${o.value}`}>
                          {o.label}
                        </option>
                      ))}
                    </optgroup>
                  </select>
                ) : null}

                {routeKind === 'venue' || routeKind === 'city' ? (
                  <div className="space-y-2">
                    {(routeKind === 'venue' && routeVenue) || (routeKind === 'city' && routeCity) ? (
                      <div className="flex items-center justify-between gap-3 rounded-lg border border-accent/45 bg-accent/10 px-3 py-2">
                        <span className="min-w-0 truncate text-sm text-white">
                          {routeKind === 'venue'
                            ? `${routeVenue!.name}${routeVenue!.cityLabel ? ` · ${routeVenue!.cityLabel}` : ''}`
                            : routeCity!.formatted}
                        </span>
                        <button
                          type="button"
                          onClick={() => {
                            setRouteVenue(null);
                            setRouteCity(null);
                            setRouteQuery('');
                          }}
                          className="shrink-0 text-xs font-medium text-muted hover:text-white"
                        >
                          Change
                        </button>
                      </div>
                    ) : (
                      <>
                        <input
                          type="text"
                          value={routeQuery}
                          onChange={(e) => setRouteQuery(e.target.value)}
                          placeholder={
                            routeKind === 'venue'
                              ? 'Search rated venues by name…'
                              : 'Search featured cities by name…'
                          }
                          className="admin-input"
                        />
                        {routeSearchBusy ? (
                          <p className="text-[11px] text-mutedDim">Searching…</p>
                        ) : null}
                        {!routeSearchBusy && routeResults.length > 0 ? (
                          <div className="max-h-56 space-y-1 overflow-y-auto">
                            {routeResults.map((r) => (
                              <button
                                key={r.id}
                                type="button"
                                onClick={() => {
                                  if ('formatted' in r) {
                                    setRouteCity(r);
                                  } else {
                                    setRouteVenue(r);
                                  }
                                }}
                                className="block w-full rounded-lg border border-border px-3 py-2 text-left text-sm text-white/90 hover:bg-white/[0.04]"
                              >
                                {'formatted' in r ? r.formatted : r.name}
                                {!('formatted' in r) && r.cityLabel ? (
                                  <span className="block text-[11px] text-mutedDim">{r.cityLabel}</span>
                                ) : null}
                              </button>
                            ))}
                          </div>
                        ) : null}
                        {!routeSearchBusy && ilikeSafe(routeQuery).length >= 2 && routeResults.length === 0 ? (
                          <p className="text-[11px] text-mutedDim">
                            {routeKind === 'venue'
                              ? 'No rated venue matches. Only venues with at least one Shado rating can be a destination — an unrated venue opens an empty page.'
                              : 'No featured city matches. Only featured cities have a city page to open.'}
                          </p>
                        ) : null}
                      </>
                    )}
                  </div>
                ) : null}

                {/* ⚠️ The honest caveat, on screen rather than in a doc nobody opens. */}
                <p className="text-[11px] leading-relaxed text-mutedDim">
                  Announcement taps have never been routed by the app — every one so far has opened the
                  notification centre. Anything other than the default needs the client patch to
                  <span className="text-white/80"> app/_layout.tsx</span> to be shipped as an OTA first;
                  until then the push still sends and still opens the notification centre, so nothing
                  breaks, it just ignores the destination.
                </p>
              </div>
            ) : null}

            {/* Audience targeting */}
            <div className="space-y-3 rounded-lg border border-border/60 bg-canvas/40 px-4 py-4">
              <p className="text-[10px] font-medium uppercase tracking-[0.14em] text-mutedDim">Audience targeting</p>

              <div className="space-y-1.5">
                <span className="text-xs text-muted">Specific users — only they receive it</span>
                <div className="flex flex-wrap items-center gap-1.5">
                  {targetUsers.map((u) => (
                    <span
                      key={u.id}
                      className="inline-flex items-center gap-1.5 rounded-full border border-accent/45 bg-accent/10 px-2.5 py-0.5 text-[11px] text-accent"
                    >
                      @{u.username}
                      <button
                        type="button"
                        onClick={() => setTargetUsers((us) => us.filter((x) => x.id !== u.id))}
                        className="text-accent/70 hover:text-white"
                        aria-label={`Remove ${u.username}`}
                      >
                        ✕
                      </button>
                    </span>
                  ))}
                  <input
                    className="admin-input !w-[180px] !text-[12px]"
                    placeholder="add by username…"
                    value={userQuery}
                    onChange={(e) => setUserQuery(e.target.value)}
                  />
                </div>
                {userResults.length > 0 ? (
                  <div className="flex flex-wrap gap-1.5">
                    {userResults.map((u) => (
                      <button
                        key={u.id}
                        type="button"
                        onClick={() => {
                          setTargetUsers((us) => [...us, u]);
                          setUserQuery('');
                          setUserResults([]);
                        }}
                        className="admin-pill"
                      >
                        + @{u.username}
                      </button>
                    ))}
                  </div>
                ) : null}
                {targetUsers.length > 0 ? (
                  <p className="text-[10.5px] leading-relaxed text-mutedDim">
                    {targetUsers.length} user{targetUsers.length === 1 ? '' : 's'} picked — the
                    filters below still apply on top (clear the frequency cap if someone was pushed
                    recently). Works with scheduling too.
                  </p>
                ) : null}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label className="space-y-1">
                  <span className="text-xs text-muted">Joined after</span>
                  <input
                    type="date"
                    value={joinedAfter}
                    onChange={(e) => setJoinedAfter(e.target.value)}
                    className="admin-input"
                  />
                </label>
                <label className="space-y-1">
                  <span className="text-xs text-muted">Joined before</span>
                  <input
                    type="date"
                    value={joinedBefore}
                    onChange={(e) => setJoinedBefore(e.target.value)}
                    className="admin-input"
                  />
                </label>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label className="space-y-1">
                  <span className="text-xs text-muted">City</span>
                  <select
                    value={cityFilter}
                    onChange={(e) => setCityFilter(e.target.value)}
                    className="admin-input"
                  >
                    <option value="">Everyone</option>
                    {profileTargets
                      .filter((t) => t.kind === 'city')
                      .map((t) => (
                        <option key={t.value} value={t.value}>
                          {t.value} ({t.n.toLocaleString()})
                        </option>
                      ))}
                  </select>
                </label>
                <label className="space-y-1">
                  <span className="text-xs text-muted">University</span>
                  <select
                    value={universityFilter}
                    onChange={(e) => setUniversityFilter(e.target.value)}
                    className="admin-input"
                  >
                    <option value="">Everyone</option>
                    {profileTargets
                      .filter((t) => t.kind === 'university')
                      .map((t) => (
                        <option key={t.value} value={t.value}>
                          {t.value} ({t.n.toLocaleString()})
                        </option>
                      ))}
                  </select>
                </label>
              </div>
              {cityFilter && universityFilter ? (
                <div className="flex items-center gap-2 text-xs text-muted">
                  <span className="text-mutedDim">Combine city + university:</span>
                  <button
                    type="button"
                    className="admin-pill"
                    data-active={cityUniLogic === 'and'}
                    onClick={() => setCityUniLogic('and')}
                    title="Only people who match BOTH — that university's people in that city"
                  >
                    AND — both
                  </button>
                  <button
                    type="button"
                    className="admin-pill"
                    data-active={cityUniLogic === 'or'}
                    onClick={() => setCityUniLogic('or')}
                    title="Anyone who matches EITHER — the whole city plus the whole university"
                  >
                    OR — either
                  </button>
                </div>
              ) : null}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label className="space-y-1">
                  <span className="text-xs text-muted">Min total venues rated</span>
                  <input
                    type="number"
                    inputMode="numeric"
                    min={0}
                    step={1}
                    placeholder="Off"
                    value={minVenuesRated}
                    onChange={(e) => setMinVenuesRated(e.target.value)}
                    className="admin-input"
                  />
                </label>
                <label className="space-y-1">
                  <span className="text-xs text-muted">Max total venues rated</span>
                  <input
                    type="number"
                    inputMode="numeric"
                    min={0}
                    step={1}
                    placeholder="Off"
                    value={maxVenuesRated}
                    onChange={(e) => setMaxVenuesRated(e.target.value)}
                    className="admin-input"
                  />
                </label>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label className="space-y-1">
                  <span className="text-xs text-muted">Min friends</span>
                  <input
                    type="number"
                    inputMode="numeric"
                    min={0}
                    step={1}
                    placeholder="Off"
                    value={minFriends}
                    onChange={(e) => setMinFriends(e.target.value)}
                    className="admin-input"
                  />
                </label>
                <label className="space-y-1">
                  <span className="text-xs text-muted">Max friends</span>
                  <input
                    type="number"
                    inputMode="numeric"
                    min={0}
                    step={1}
                    placeholder="Off"
                    value={maxFriends}
                    onChange={(e) => setMaxFriends(e.target.value)}
                    className="admin-input"
                  />
                </label>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label className="space-y-1">
                  <span className="text-xs text-muted">No rating in the last … days (had rated)</span>
                  <input
                    type="number"
                    inputMode="numeric"
                    min={1}
                    step={1}
                    placeholder="Off"
                    value={lastRatingDays}
                    onChange={(e) => setLastRatingDays(e.target.value)}
                    className="admin-input"
                  />
                </label>
                <label className="space-y-1">
                  <span className="text-xs text-muted">Hasn’t opened the app in … days</span>
                  <input
                    type="number"
                    inputMode="numeric"
                    min={1}
                    step={1}
                    placeholder="Off"
                    value={lastSeenDays}
                    onChange={(e) => setLastSeenDays(e.target.value)}
                    className="admin-input"
                  />
                </label>
              </div>
              <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
                <label className="flex items-center gap-2 text-xs text-muted cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={reachableOnly}
                    onChange={(e) => setReachableOnly(e.target.checked)}
                    className="h-3.5 w-3.5 rounded border-border bg-canvas text-accent"
                  />
                  Only users reachable by push
                </label>
                {/* Stamp targeting (Evan 2026-09-08): mirrors the app's profile stamps, computed
                    server-side from the same logs — see announcement_stamp_targeting_v6.sql. */}
                <label className="flex items-center gap-2 text-xs text-muted">
                  Has stamp
                  <select
                    value={stampFilter}
                    onChange={(e) => setStampFilter(e.target.value)}
                    className="admin-input !w-auto !px-2 !text-xs"
                  >
                    <option value="">Any</option>
                    <option value="first_round">Cheers — rated 1+ venue</option>
                    <option value="night_owl">Regular — 10+ venues</option>
                    <option value="scene_veteran">Night Owl — 50+ venues</option>
                    <option value="out_of_town">Traveler — 3+ cities</option>
                    <option value="traveler">Frequent Flyer — 10+ cities</option>
                    <option value="worldwide">Global Citizen — 20+ cities</option>
                    <option value="bar_hopper">Bar Hopper — 20+ bars</option>
                    <option value="disco">Clubber — 20+ clubs</option>
                    <option value="icon">Elite — 50 bars + 50 clubs</option>
                  </select>
                </label>
                {/**
                 * FREE ENTRY, NOT PRESETS (Evan 2026-08-15: "in ADMIN-DASH I should be able to
                 * customize this"). This was a four-option <select> — Off / 3 / 7 / 14 — and it was
                 * the ONLY numeric control on this panel that was not free entry: its neighbours
                 * ("No rating in the last … days", "Hasn't opened the app in … days", "Skip users
                 * with N+ lifetime pushes") are all plain number inputs. A 10-day cap was simply
                 * unreachable for no reason anyone could see.
                 *
                 * ⚠️ NOTHING DOWNSTREAM NEEDED TO CHANGE. The value was already a string parsed
                 * through `parseVenuesInput`, which returns null for empty/non-numeric/negative and
                 * truncates the rest — so blank still means Off exactly as the removed option did.
                 *
                 * Later same day: hours|days unit selector added; the v5 RPCs take
                 * p_not_pushed_within_hours, and days convert to hours client-side.
                 */}
                <label className="flex items-center gap-2 text-xs text-muted">
                  Skip if already pushed within
                  <input
                    type="number"
                    inputMode="numeric"
                    min={1}
                    step={1}
                    placeholder="Off"
                    value={freqCapValue}
                    onChange={(e) => setFreqCapValue(e.target.value)}
                    className="admin-input !w-16 !px-2 !text-xs"
                  />
                  <select
                    value={freqCapUnit}
                    onChange={(e) => setFreqCapUnit(e.target.value as 'hours' | 'days')}
                    className="admin-input !w-auto !px-2 !text-xs"
                  >
                    <option value="hours">hours</option>
                    <option value="days">days</option>
                  </select>
                </label>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label className="space-y-1">
                  <span className="text-xs text-muted">Campaign key (powers dedupe + outcome tracking)</span>
                  <input
                    type="text"
                    placeholder="e.g. never-rated-v1"
                    value={campaign}
                    onChange={(e) => setCampaign(e.target.value)}
                    className="admin-input"
                  />
                </label>
                <label className="space-y-1">
                  <span className="text-xs text-muted">Retarget: only users who received…</span>
                  <select
                    value={retargetCampaign}
                    onChange={(e) => setRetargetCampaign(e.target.value)}
                    className="admin-input"
                  >
                    <option value="">Off</option>
                    {knownCampaigns.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
                <label
                  className={`flex items-center gap-2 text-xs cursor-pointer select-none ${
                    campaign.trim() ? 'text-muted' : 'text-mutedDim'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={dedupeSameCampaign}
                    disabled={!campaign.trim()}
                    onChange={(e) => setDedupeSameCampaign(e.target.checked)}
                    className="h-3.5 w-3.5 rounded border-border bg-canvas text-accent"
                  />
                  Never send this campaign to the same user twice
                </label>
                <label className="flex items-center gap-2 text-xs text-muted">
                  Skip users with
                  <input
                    type="number"
                    inputMode="numeric"
                    min={1}
                    step={1}
                    placeholder="∞"
                    value={maxPushesTotal}
                    onChange={(e) => setMaxPushesTotal(e.target.value)}
                    className="admin-input !w-16 !px-2 !text-xs"
                  />
                  + lifetime pushes
                </label>
              </div>
              <p className="text-xs text-mutedDim">
                {audienceCountBusy
                  ? 'Counting…'
                  : audienceCount !== null
                  ? `Will reach ~${audienceCount.toLocaleString()} users`
                  : 'Leave all filters empty to reach everyone.'}
              </p>
              {!audienceCountBusy &&
              audienceCount === 0 &&
              parseVenuesInput(freqCapValue) != null &&
              parseVenuesInput(freqCapValue)! > 0 ? (
                <p className="text-[11px] text-mutedDim">
                  0 usually means everyone matching was already pushed within the last{' '}
                  {freqCapValue} {freqCapUnit} — shorten the window (try hours) or set it Off to see
                  the pool reopen.
                </p>
              ) : null}
              {samples.length > 0 ? (
                <div className="rounded-md border border-border/50 bg-canvas/60 px-3 py-2.5">
                  <p className="mb-1.5 text-[10px] font-medium uppercase tracking-[0.12em] text-mutedDim">
                    Sample of who this reaches
                  </p>
                  <div className="space-y-1">
                    {samples.map((s, i) => (
                      <div key={`${s.username ?? i}`} className="flex flex-wrap items-baseline gap-x-3 text-[11px]">
                        <span className="font-medium text-muted">@{s.username ?? '—'}</span>
                        <span className="text-mutedDim">joined {timeAgo(s.joined_at)}</span>
                        <span className="text-mutedDim">
                          {s.last_rated_at ? `rated ${timeAgo(s.last_rated_at)}` : 'never rated'}
                        </span>
                        <span className="text-mutedDim">
                          {s.last_seen_at ? `seen ${timeAgo(s.last_seen_at)}` : 'no session on record'}
                        </span>
                        <span className="text-mutedDim">
                          {Number(s.pushes_received ?? 0)} push{Number(s.pushes_received ?? 0) === 1 ? '' : 'es'} so far
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>

            <div>
              <button
                type="button"
                disabled={userCountBusy}
                onClick={() => void insertUserCount()}
                className="rounded-lg border border-border px-3 py-1.5 text-xs font-medium text-muted hover:text-white hover:bg-white/[0.04] disabled:opacity-50"
              >
                {userCountBusy ? 'Counting…' : 'Add current user count'}
              </button>
            </div>

            <p className="text-[11px] text-mutedDim">
              Line breaks are preserved. Goes live immediately for users who have not dismissed it yet.
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <button
                type="button"
                disabled={busy || !draft.trim() || (sendAsPush && routeIncomplete)}
                onClick={() => (sendAsPush ? void openPushConfirm() : void publish())}
                className={`rounded-lg px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-40 ${
                  blocking ? 'bg-red-600 hover:bg-red-500' : 'bg-accent hover:opacity-95'
                }`}
              >
                {busy
                  ? sendAsPush
                    ? 'Sending…'
                    : 'Publishing…'
                  : sendAsPush
                    ? 'Send push'
                    : blocking
                      ? 'Publish app-blocking lock'
                      : 'Publish to all users'}
              </button>
              {sendAsPush ? (
                <button
                  type="button"
                  disabled={testBusy || !draft.trim() || routeIncomplete}
                  onClick={() => void sendTestToMe()}
                  className="rounded-lg border border-border px-4 py-2.5 text-sm font-medium text-muted hover:text-white hover:bg-white/[0.04] disabled:opacity-40"
                >
                  {testBusy ? 'Sending test…' : 'Send test to me first'}
                </button>
              ) : null}
            </div>

            {sendAsPush ? (
              <div className="flex flex-wrap items-center gap-3 rounded-md border border-border/50 bg-canvas/60 px-3 py-2.5">
                <span className="text-[10px] font-medium uppercase tracking-[0.12em] text-mutedDim">
                  Or schedule it
                </span>
                <input
                  type="datetime-local"
                  value={scheduleAt}
                  onChange={(e) => setScheduleAt(e.target.value)}
                  className="rounded-md border border-border bg-canvas px-2.5 py-1.5 text-xs text-white [color-scheme:dark]"
                />
                <button
                  type="button"
                  disabled={scheduleBusy || !draft.trim() || routeIncomplete || !scheduleAt}
                  onClick={() => void schedulePush()}
                  className="rounded-lg border border-border px-4 py-1.5 text-xs font-medium text-muted hover:text-white hover:bg-white/[0.04] disabled:opacity-40"
                >
                  {scheduleBusy ? 'Scheduling…' : 'Schedule push'}
                </button>
                <span className="text-[11px] text-mutedDim">
                  Fires within a minute of the time. Audience = whoever matches the filters at that moment.
                </span>
                {scheduledUnavailable ? (
                  <span className="text-[11px] text-amber-300">
                    Not live yet — scheduled_announcement_pushes_v1.sql has not been applied.
                  </span>
                ) : null}
              </div>
            ) : null}
          </section>

          {scheduled.length > 0 ? (
            <section className="rounded-xl border border-border bg-surface p-6 space-y-3">
              <h2 className="text-[10px] font-medium uppercase tracking-[0.14em] text-mutedDim">
                Scheduled pushes
              </h2>
              <div className="space-y-2">
                {scheduled.map((row) => {
                  const p = row.params ?? {};
                  const bits = [
                    typeof p.p_city === 'string' && p.p_city ? `city: ${p.p_city}` : null,
                    typeof p.p_university === 'string' && p.p_university ? `uni: ${p.p_university}` : null,
                    typeof p.p_campaign === 'string' && p.p_campaign ? `campaign: ${p.p_campaign}` : null,
                  ].filter(Boolean);
                  // Destination in words, mirrored from the stored p_route (the same shapes
                  // routePayload() writes) — so the preview reads like the composer's.
                  const route = (p.p_route ?? null) as Record<string, unknown> | null;
                  const destination = !route
                    ? 'the notification centre'
                    : route.kind === 'tab'
                      ? `the ${String(route.tab ?? '')} tab`
                      : route.kind === 'venue'
                        ? `${String(route.venue_name ?? 'a venue')} — venue page`
                        : route.kind === 'city'
                          ? `${String(route.city_formatted_name ?? route.city_name ?? 'a city')} — city page`
                          : 'the notification centre';
                  return (
                    <div key={row.id} className="rounded-md border border-border/50 bg-canvas/60 px-3 py-2.5">
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                        <span
                          className={`rounded-md px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
                            row.status === 'scheduled'
                              ? 'border border-accent/45 bg-accent/15 text-accent'
                              : row.status === 'sent'
                                ? 'border border-emerald-500/45 bg-emerald-500/15 text-emerald-300'
                                : row.status === 'failed'
                                  ? 'border border-red-500/45 bg-red-500/15 text-red-300'
                                  : 'border border-border text-mutedDim'
                          }`}
                        >
                          {row.status}
                        </span>
                        <span className="text-xs text-white/90">{new Date(row.run_at).toLocaleString()}</span>
                        {bits.length > 0 ? (
                          <span className="text-[11px] text-mutedDim">{bits.join(' · ')}</span>
                        ) : null}
                        {(row.status === 'sent' || row.status === 'failed') && row.result ? (
                          <span className="text-[11px] text-mutedDim">{row.result}</span>
                        ) : null}
                        {row.status === 'scheduled' ? (
                          <button
                            type="button"
                            disabled={cancelBusyId === row.id}
                            onClick={() => void cancelScheduled(row.id)}
                            className="ml-auto rounded-md border border-border px-2.5 py-1 text-[11px] font-medium text-muted hover:text-white hover:bg-white/[0.04] disabled:opacity-50"
                          >
                            {cancelBusyId === row.id ? 'Cancelling…' : 'Cancel'}
                          </button>
                        ) : null}
                      </div>
                      {/* Lock-screen preview — same two lines the composer shows. */}
                      <div className="mt-2 rounded-lg border border-border/60 bg-canvas/80 px-4 py-3">
                        <p className="text-[10px] font-medium uppercase tracking-[0.14em] text-mutedDim">
                          Lock screen
                        </p>
                        <p className="mt-2 text-sm font-semibold text-white">
                          {typeof p.p_title === 'string' && p.p_title.trim() ? p.p_title : 'shado'}
                        </p>
                        <p className="whitespace-pre-wrap text-sm leading-snug text-white/80">
                          {typeof p.p_body === 'string' ? p.p_body : ''}
                        </p>
                        <p className="mt-2 text-[11px] text-mutedDim">Tapping it opens {destination}.</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          ) : null}

          

          

          

          <section className="rounded-xl border border-border bg-surface p-6 space-y-4">
            <h2 className="text-[10px] font-medium uppercase tracking-[0.14em] text-mutedDim">Currently live</h2>
            {active ? (
              <div className="space-y-3">
                <div className="flex flex-wrap items-center gap-2">
                  {active.blocking ? (
                    <span className="rounded-md border border-red-500/45 bg-red-500/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-red-300">
                      Blocking — app locked
                    </span>
                  ) : (
                    <span className="rounded-md border border-accent/45 bg-accent/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-accent">
                      Dismissible
                    </span>
                  )}
                  {active.require_terms ? (
                    <span className="rounded-md border border-emerald-500/45 bg-emerald-500/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-300">
                      Terms required
                    </span>
                  ) : null}
                </div>
                <p className="text-sm text-white/95 whitespace-pre-wrap leading-relaxed">{active.body}</p>
                <div className="text-xs text-mutedDim space-y-1.5">
                  <p>Published {timeAgo(active.created_at)}</p>
                  <DismissalRate
                    dismissed={Number(active.dismissal_count)}
                    total={totalUsers}
                    className="block"
                  />
                </div>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => void endActive()}
                  className={`rounded-lg px-4 py-2 text-sm font-semibold disabled:opacity-50 ${
                    active.blocking
                      ? 'bg-accent text-white hover:opacity-95'
                      : 'border border-border text-white hover:bg-white/[0.04]'
                  }`}
                >
                  {active.blocking ? 'End lock & unlock the app' : 'End announcement'}
                </button>
              </div>
            ) : (
              <p className="text-sm text-muted">No announcement is live right now.</p>
            )}
          
            <div className="border-t border-border/60 pt-4">
              <div className="mb-3 flex items-center gap-1.5">
                <button
                  type="button"
                  data-active={historyTab === 'push'}
                  onClick={() => setHistoryTab('push')}
                  className="admin-pill"
                >
                  Push history
                </button>
                <button
                  type="button"
                  data-active={historyTab === 'in-app'}
                  onClick={() => setHistoryTab('in-app')}
                  className="admin-pill"
                >
                  In-app history
                </button>
              </div>
              {historyTab === 'in-app' ? (
                history.length === 0 ? (
                  <p className="text-sm text-muted">No in-app announcements yet.</p>
                ) : (
                  <ul className="rounded-xl border border-border bg-surface divide-y divide-border/50 overflow-hidden">
                    {[...history]
                      .sort((a, b) => +new Date(b.created_at) - +new Date(a.created_at))
                      .map((r) => (
                        <li key={r.id} className="px-5 py-4">
                          <div className="flex flex-wrap items-center gap-2 mb-1.5">
                            <span className="text-xs text-mutedDim">{timeAgo(r.created_at)}</span>
                            {r.is_active ? (
                              <span className="text-[10px] uppercase tracking-wider text-accent font-medium">
                                Live
                              </span>
                            ) : null}
                            {r.blocking ? (
                              <span className="text-[10px] uppercase tracking-wider text-red-300 font-medium">
                                Blocking
                              </span>
                            ) : null}
                            <button
                              type="button"
                              onClick={() => reuseAsDraft(r.body)}
                              className="ml-auto text-[11px] font-medium text-accent hover:underline"
                            >
                              Reuse as draft
                            </button>
                          </div>
                          <p className="text-sm text-muted leading-relaxed">{truncate(r.body, 200)}</p>
                          <DismissalRate
                            dismissed={Number(r.dismissal_count)}
                            total={totalUsers}
                            className="mt-2 block text-[10px] text-mutedDim"
                          />
                        </li>
                      ))}
                  </ul>
                )
              ) : (
                pushHistoryUnavailable ? (
              <div className="rounded-lg border border-border/60 bg-canvas/40 px-4 py-3 text-xs text-mutedDim leading-relaxed">
                Push history is not tracked yet — run supabase/sql/push_announcement_log.sql against production to
                enable it. Pushes still send; they just aren't logged here until then.
              </div>
            ) : pushHistoryLoading ? (
              <SkeletonRows rows={3} />
            ) : pushHistory.length === 0 ? (
              <p className="text-sm text-muted">No push sends yet.</p>
            ) : (
              <ul className="rounded-xl border border-border bg-surface divide-y divide-border/50 overflow-hidden">
                {[...pushHistory].sort((a, b) => +new Date(b.sent_at) - +new Date(a.sent_at)).map((r) => (
                  <li key={r.id} className="px-5 py-4">
                    <div className="flex flex-wrap items-center gap-2 mb-1.5">
                      <span className="text-xs text-mutedDim">{timeAgo(r.sent_at)}</span>
                      {r.campaign ? (
                        <span className="rounded-md border border-accent/40 bg-accent/10 px-1.5 py-0.5 text-[10px] font-medium text-accent">
                          {r.campaign}
                        </span>
                      ) : null}
                      {r.is_test ? (
                        <span className="rounded-md border border-amber-500/40 bg-amber-500/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-amber-300">
                          Test
                        </span>
                      ) : null}
                      <span className="text-[10px] uppercase tracking-wider text-accent font-medium">
                        {r.recipient_count.toLocaleString()} recipient{r.recipient_count === 1 ? '' : 's'}
                      </span>
                      {r.target_city ? (
                        <span className="rounded-md border border-border bg-canvas/60 px-1.5 py-0.5 text-[10px] text-muted">
                          {r.target_city}
                        </span>
                      ) : null}
                      {r.target_university ? (
                        <span className="rounded-md border border-border bg-canvas/60 px-1.5 py-0.5 text-[10px] text-muted">
                          {r.target_university}
                        </span>
                      ) : null}
                      <button
                        type="button"
                        onClick={() => reuseAsDraft(r.body)}
                        className="ml-auto text-[11px] font-medium text-accent hover:underline"
                      >
                        Reuse as draft
                      </button>
                    </div>
                    <p className="text-sm text-muted leading-relaxed">{truncate(r.body, 200)}</p>
                    {r.attributed && r.recipient_count > 0 && !r.is_test ? (
                      <p className="mt-2 text-[11px] tabular-nums text-mutedDim">
                        <span className="text-emerald-300/90">
                          {r.opened_72h.toLocaleString()} opened within 72h (
                          {Math.round((r.opened_72h / r.recipient_count) * 100)}%)
                        </span>
                        {' · '}
                        <span className="text-accent/90">
                          {r.rated_7d.toLocaleString()} rated within 7d (
                          {Math.round((r.rated_7d / r.recipient_count) * 100)}%)
                        </span>
                      </p>
                    ) : !r.attributed && !r.is_test ? (
                      <p className="mt-2 text-[11px] text-mutedDim">
                        Outcomes n/a — sent before campaign tracking.
                      </p>
                    ) : null}
                  </li>
                ))}
              </ul>
            )
              )}
            </div>
          </section>
        </div>
      )}

      <ConfirmModal
        open={confirmBlockingOpen}
        danger
        title="Block the app for everyone?"
        message="Blocking mode publishes a full-screen message that users CANNOT dismiss — it prevents everyone from using the app until you end it. Use this only for maintenance or outages. Enable blocking mode for this announcement?"
        confirmLabel="Yes, enable blocking"
        cancelLabel="Cancel"
        onConfirm={() => {
          setBlocking(true);
          setConfirmBlockingOpen(false);
        }}
        onCancel={() => setConfirmBlockingOpen(false)}
      />

      <ConfirmModal
        open={confirmPushOpen}
        danger
        title={`Send push to ${pushAudience != null ? `~${pushAudience.toLocaleString()}` : 'the selected'} user${pushAudience === 1 ? '' : 's'}?`}
        message={`This sends the message as a push notification to ${
          pushAudience != null
            ? `~${pushAudience.toLocaleString()} user${pushAudience === 1 ? '' : 's'}`
            : 'the targeted audience'
        }${
          /* ⚠️ EVERY audience filter must be listed here, or the confirm claims a targeted send
             reaches EVERYONE — the one sentence you rely on before pushing to real users. */
          !cityFilter && !universityFilter && !joinedBefore && !joinedAfter &&
          !minVenuesRated.trim() && !maxVenuesRated.trim() &&
          !minFriends.trim() && !maxFriends.trim()
            ? ' — no filters are set, so this reaches EVERYONE.'
            : ' matching your audience filters.'
        }${
          parseVenuesInput(freqCapValue) != null && parseVenuesInput(freqCapValue)! > 0
            ? ` Anyone already pushed in the last ${freqCapValue} ${freqCapUnit} is skipped automatically.`
            : ' The pushed-recently cap is OFF — users may be pushed twice.'
        } It reads "${title.trim() || 'shado'}" over "${truncate(draft, 60)}" and opens ${routeSummary} when tapped. It cannot be undone — confirm once.`}
        confirmLabel="Send push"
        cancelLabel="Cancel"
        onConfirm={() => {
          setConfirmPushOpen(false);
          void doPushSend();
        }}
        onCancel={() => setConfirmPushOpen(false)}
      />
    </div>
  );
}
